/**
 * The Strip Card board's view model (ISSUE-1288, ISSUE-1296). Every decision
 * here reads a server field: `canClaim`, `claimBlockedReason`, `isMine`,
 * `state`, `isCanceled` and `viewer.canManage`. Nothing counts rows against a
 * cap or decides who may lock; the server already did (server resolves,
 * client renders). What lives here is which sheet a tap opens, and the
 * optimistic paint of a claim before the server answers.
 */
import type {
  useStripsCardGet,
  useStripsPoolGet,
} from "@sportsmet/contest-sdk/private";

import {
  STRIP_NOTE_GAME_CANCELED,
  STRIP_NOTE_LIMIT_REACHED,
  STRIP_NOTE_SOMEONE_ELSE,
  STRIP_NOTE_STRIP_LOCKED,
  STRIP_YOU_LABEL,
} from "./stripCardCopy";

export type typeStripCardEnvelope = NonNullable<
  ReturnType<typeof useStripsCardGet>["data"]
>;
export type typeStripCardData = NonNullable<typeStripCardEnvelope["data"]>;
export type typeStripView = typeStripCardData["strips"][number];
export type typeStripRowView = typeStripView["rows"][number];
export type typeStripGameView = typeStripCardData["game"];
export type typeStripPoolData = NonNullable<
  NonNullable<ReturnType<typeof useStripsPoolGet>["data"]>["data"]
>;
export type typeStripPoolGame = typeStripPoolData["games"][number];

/** The generated card-get query key, segment for segment. Used to paint an
 *  optimistic claim into the exact cache entry the board reads. */
export function stripCardQueryKey(input: {
  poolId: string;
  eventId: string;
}): string[] {
  return [
    "post:strips:stripsCardGet",
    "svc:contest-api",
    `poolId:${JSON.stringify(input.poolId)}`,
    `eventId:${JSON.stringify(input.eventId)}`,
  ];
}

/** The strip the board shows in its members column: the one picked, while it
 *  still exists, else the first strip. Null on a card with no strips. */
export function resolveSelectedStripNumber(input: {
  strips: readonly { stripNumber: number }[];
  picked: number | null;
}): number | null {
  if (
    input.picked !== null &&
    input.strips.some((strip) => strip.stripNumber === input.picked)
  ) {
    return input.picked;
  }
  return input.strips[0]?.stripNumber ?? null;
}

/** The game the tab opens: the one picked while the pool still has it, else
 *  the server's `selectedEventId`, else the first game. */
export function resolveSelectedEventId(input: {
  games: readonly { eventId: string }[];
  serverSelectedEventId: string | null | undefined;
  picked: string | null;
  /** The game an alert link names (the pools route's eventId). */
  linked?: string | null | undefined;
}): string | null {
  const has = (eventId: string | null | undefined): eventId is string =>
    typeof eventId === "string" &&
    input.games.some((game) => game.eventId === eventId);
  if (has(input.picked)) return input.picked;
  if (has(input.linked)) return input.linked;
  if (has(input.serverSelectedEventId)) return input.serverSelectedEventId;
  return input.games[0]?.eventId ?? null;
}

export type typeMemberCellAction =
  | { kind: "claim" }
  | { kind: "release" }
  | { kind: "assign" }
  | { kind: "note"; note: string };

/** What a tap on a row of the members column does. */
export function resolveMemberCellAction(input: {
  isCardCanceled: boolean;
  canManage: boolean;
  strip: Pick<typeStripView, "state" | "canClaim" | "claimBlockedReason">;
  row: Pick<typeStripRowView, "userId" | "isMine">;
}): typeMemberCellAction {
  if (input.isCardCanceled) {
    return { kind: "note", note: STRIP_NOTE_GAME_CANCELED };
  }
  if (input.strip.state === "locked") {
    return { kind: "note", note: STRIP_NOTE_STRIP_LOCKED };
  }
  if (input.canManage) return { kind: "assign" };
  const holder = input.row.userId ?? null;
  if (holder === null) {
    if (input.strip.canClaim) return { kind: "claim" };
    return { kind: "note", note: claimBlockedNote(input.strip) };
  }
  if (input.row.isMine) return { kind: "release" };
  return { kind: "note", note: STRIP_NOTE_SOMEONE_ELSE };
}

function claimBlockedNote(
  strip: Pick<typeStripView, "claimBlockedReason">,
): string {
  switch (strip.claimBlockedReason) {
    case "stripLocked":
      return STRIP_NOTE_STRIP_LOCKED;
    case "gameCanceled":
      return STRIP_NOTE_GAME_CANCELED;
    case "limitReached":
    default:
      return STRIP_NOTE_LIMIT_REACHED;
  }
}

export type typeDigitCellAction =
  { kind: "editWinners" } | { kind: "viewWinners" } | { kind: "select" };

/** What a tap on a digit cell does. An admin marks winners on a locked row;
 *  a member can read a row's winners; anything else just selects the strip. */
export function resolveDigitCellAction(input: {
  isCardCanceled: boolean;
  canManage: boolean;
  strip: Pick<typeStripView, "state">;
  row: Pick<typeStripRowView, "winners">;
}): typeDigitCellAction {
  if (input.isCardCanceled || input.strip.state !== "locked") {
    return { kind: "select" };
  }
  if (input.canManage) return { kind: "editWinners" };
  return input.row.winners.length > 0
    ? { kind: "viewWinners" }
    : { kind: "select" };
}

/** The name the members column shows for a row, or null for an open row. */
export function rowHolderName(row: typeStripRowView): string | null {
  if (row.userId === null || row.userId === undefined) return null;
  if (row.isMine) return STRIP_YOU_LABEL;
  return row.displayName ?? null;
}

/**
 * Paints a claim or a release into a cached card before the server answers.
 * Only the tapped row changes; the counts wait for the server's refetch,
 * because a count is the server's to compute. Returns the same object when
 * the strip or row is not on the card.
 */
export function applyOptimisticSpot(
  data: typeStripCardData,
  change: {
    stripNumber: number;
    row: number;
    kind: "claim" | "release";
  },
): typeStripCardData {
  const stripIndex = data.strips.findIndex(
    (strip) => strip.stripNumber === change.stripNumber,
  );
  if (stripIndex === -1) return data;
  const strip = data.strips[stripIndex];
  if (strip === undefined) return data;
  const rowIndex = strip.rows.findIndex((row) => row.row === change.row);
  if (rowIndex === -1) return data;
  const row = strip.rows[rowIndex];
  if (row === undefined) return data;
  const nextRow: typeStripRowView =
    change.kind === "claim"
      ? {
          ...row,
          userId: data.viewer.userId,
          displayName: STRIP_YOU_LABEL,
          initials: row.initials ?? null,
          isMine: true,
        }
      : {
          ...row,
          userId: null,
          displayName: null,
          initials: null,
          avatarUrl: null,
          isMine: false,
        };
  const rows = strip.rows.map((candidate, index) =>
    index === rowIndex ? nextRow : candidate,
  );
  const strips = data.strips.map((candidate, index) =>
    index === stripIndex ? { ...strip, rows } : candidate,
  );
  return { ...data, strips };
}

/** The poll interval react-query takes from a read's `refreshAfterSeconds`:
 *  false (no polling) when the server asks for none. */
export function stripRefreshIntervalMs(
  refreshAfterSeconds: number | null | undefined,
): number | false {
  return typeof refreshAfterSeconds === "number" && refreshAfterSeconds > 0
    ? refreshAfterSeconds * 1000
    : false;
}

/** A fresh id for one Add a strip tap. A retry of the same tap reuses it, so
 *  the server's `transactionId` check answers with the strip it already
 *  added instead of adding a second one. */
export function newStripTransactionId(): string {
  return crypto.randomUUID();
}
